import { Dispatch, FunctionComponent, SetStateAction } from "react";
import { useTranslation } from "react-i18next";
import { Box, Stack, TextField } from "@mui/material";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";

import { Modal } from "@/components";
import {
	useDisableTwoFactorMutation,
	useVerifyTwoFactorMutation,
} from "@/pages/Private/redux/account/account.api";
import {
	TwoFactorAuth,
	TwoFactorAuthSchema,
} from "@/pages/Public/pages/TwoFactorAuth/schema/twoFactorAuth";
import { ERROR_TYPE, getAllErrors, renderErrorMessages } from "@/utils";

interface Enable2FAModalProps {
	isOpen: boolean;
	qrCode: string;
	setIsOpen: Dispatch<SetStateAction<boolean>>;
	isLoading: boolean;
	isRegistration?: boolean;
	onClose?: () => void;
}

export const Enable2FAModal: FunctionComponent<Enable2FAModalProps> = ({
	isOpen,
	qrCode,
	setIsOpen,
	isLoading,
	isRegistration = false,
	onClose,
}) => {
	const { t } = useTranslation();
	const ts = (key: string) => t(`account.${key}`);

	const [disable, { isLoading: isDisabling, error: disableError }] = useDisableTwoFactorMutation();

	const {
		register,
		handleSubmit,
		formState: { errors },
	} = useForm({
		defaultValues: {
			code: "",
			token: "",
		},
		resolver: zodResolver(TwoFactorAuthSchema),
	});

	const [verifySettings, { isLoading: isVerifying, error: verifyError }] =
		useVerifyTwoFactorMutation();

	const formErrors = Object.values(errors).map((error) => error.message) as ERROR_TYPE[];

	const handleClose = () => {
		setIsOpen(false);
		onClose?.();
	};

	const onSubmit = async ({ code }: TwoFactorAuth) => {
		try {
			if (qrCode) {
				await verifySettings({ code }).unwrap();
				setIsOpen(false);
				onClose?.();
			} else {
				await disable({ code }).unwrap();
				setIsOpen(false);
			}
		} catch (err) {
			console.error(err);
		}
	};

	return (
		<Modal
			handleClose={handleClose}
			handleSave={handleSubmit(onSubmit)}
			isLoading={isLoading || isVerifying || isDisabling}
			isOpened={isOpen}
			submitButtonText={t("basics.saveChanges")}
			subtitle={
				isRegistration
					? ts("registerWithTwoFactorAuthDescription")
					: qrCode
						? ts("enableTwoFactorAuthDescription")
						: ts("disableTwoFactorAuthDescription")
			}
			title={qrCode ? ts("enableTwoFactorAuth") : ts("disableTwoFactorAuth")}
		>
			{qrCode && (
				<Stack alignItems="center" maxHeight={220}>
					<Box component="img" src={qrCode} />
				</Stack>
			)}

			<Box className="mt-5" component="form" onSubmit={handleSubmit(onSubmit)}>
				<TextField fullWidth label={t("basics.code")} {...register("code")} error={!!verifyError} />
				<button style={{ display: "none" }} type="submit" />
			</Box>

			{getAllErrors(verifyError || disableError, formErrors).length ? (
				<Box sx={{ mt: 2 }}>
					{renderErrorMessages(getAllErrors(verifyError || disableError, formErrors))}
				</Box>
			) : null}
		</Modal>
	);
};
